import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { captureException } from '@sentry/nextjs';
import styled from 'styled-components';
import { spacer12 } from '@spotify-internal/encore-web';

import { DocumentLink } from 'lib/sanity/types';
import analytics from 'utils/client/analytics/analytics';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import useShouldUseSemanticSearch from 'utils/semantic';

import { getMinRequiredChars } from './search-utils';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';

const Container = styled.div`
  padding: ${spacer12} 0;
`;

export interface SearchProps {
  ubi: UbiHomePage;
}

const Search = ({ ubi }: SearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentLink[]>([]);
  const [debouncedQuery] = useDebounce(searchQuery, 500);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const {
    verticalConfig: { verticalName },
    market: { marketCode, locale },
  } = useServerSideConstants();

  const shouldUseSemanticSearch = useShouldUseSemanticSearch();

  const minRequiredChars = getMinRequiredChars(locale);
  const enteredMinSearchChars = debouncedQuery.length >= minRequiredChars;

  useEffect(() => {
    const fetchData = async (query: string) => {
      setIsSearching(true);
      ubi.logSearch(query);

      const queryParamsObj: Record<string, string> = {
        q: query,
      };

      // remote-config doesn't work in API route, we have do it on the client side.
      if (shouldUseSemanticSearch) {
        // Make it implicit for not leaking info
        queryParamsObj.exp = '1';
      }

      const queryString = new URLSearchParams(queryParamsObj).toString();

      try {
        const response = await fetch(`/api/web/vertical/${verticalName}/${marketCode}/search/?${queryString}`);
        if (!response.ok) {
          throw new Error(`Search API Error with status: ${response.status.toString()}`);
        }
        analytics.trackVirtualPageView(`/search/?q=${query}`);
        const results = await response.json();
        setSearchResults(results);
        setIsSearching(false);
        setShowResults(true);
      } catch (error) {
        setSearchResults([]);
        setIsSearching(false);
        setShowResults(true);
        captureException(error);
      }
    };
    if (enteredMinSearchChars) {
      fetchData(debouncedQuery);
    } else if (!enteredMinSearchChars) {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [debouncedQuery, enteredMinSearchChars, marketCode, ubi, verticalName, shouldUseSemanticSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <Container>
      <SearchInput
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        clearSearch={clearSearch}
      />
      {showResults && <SearchResults searchResults={searchResults} ubi={ubi} />}
    </Container>
  );
};

export default Search;
